import React, { useState, useEffect, useRef } from "react";
import { List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { Workspace, DropdownChevronRight } from "@vibe/icons";
import { Search } from "@vibe/core";
import { useSearchList } from "../../hooks/ui";
import { useWorkspaceBoards } from "../../hooks/api";
import { CSSTransition } from "react-transition-group";
import Spinner from "../Spinner";
import BoardsList from "./BoardsList";
import AddItemSubtitle from "./AddItemSubtitle";
import type { WorkspaceListProps as WorkspaceListPropsType } from "./addItem.types";
import { WorkspaceType } from "types/workspace.list.interface";
import { MondayColors } from "@data/mondayColors";

const STICKY_HEADER_CLASS =
  "bg-[#FFFFFF] sticky top-0 z-[1] border-b border-[#C3C6D4] overflow-x-hidden";
const HEADER_PADDING = { padding: "15px 8px 12px 8px" };

const LIST_ITEM_TYPOGRAPHY = {
  fontSize: "0.875rem",
  fontFamily: "Poppins",
  whiteSpace: "nowrap" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const WorkspaceIcon = ({
  workspace,
  index,
  containerSize = 20,
  iconSize = 16,
}: {
  workspace: WorkspaceType;
  index: number;
  containerSize?: number;
  iconSize?: number;
}) => {
  const CONTAINER_SIZE = containerSize;

  const ICON_CONTAINER_STYLE: React.CSSProperties = {
    width: CONTAINER_SIZE,
    height: CONTAINER_SIZE,
    minWidth: CONTAINER_SIZE,
    minHeight: CONTAINER_SIZE,
    maxWidth: CONTAINER_SIZE,
    maxHeight: CONTAINER_SIZE,
    borderRadius: 4,
    flexShrink: 0,
    padding: 0,
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
  if (workspace.settings?.icon?.image) {
    return (
      <div style={{ ...ICON_CONTAINER_STYLE, backgroundColor: workspace.settings.icon.color }}>
        <img
          src={workspace.settings.icon.image}
          alt={workspace.name}
          style={{
            width: "80%",
            height: "80%",
            objectFit: "contain",
            display: "block",
            borderRadius: 2,
          }}
        />
      </div>
    );
  }

  if (workspace.settings?.icon?.color) {
    return (
      <div
        className="flex justify-center items-center"
        style={{ ...ICON_CONTAINER_STYLE, backgroundColor: workspace.settings.icon.color }}
      >
        <span className="font-bold text-white uppercase" style={{ fontSize: iconSize }}>
          {workspace.name.charAt(0)}
        </span>
      </div>
    );
  }
  return (
    <div
      className="flex justify-center items-center"
      style={{
        ...ICON_CONTAINER_STYLE,
        backgroundColor: MondayColors[index % MondayColors.length],
      }}
    >
      <Workspace size={iconSize} color="#ffffff" />
    </div>
  );
};

const WorkspaceList = ({
  data,
  calcHeight,
  options = {},
  onGroupSelect,
}: WorkspaceListPropsType) => {
  const [search, setSearch] = useState("");
  const [activeMenu, setActiveMenu] = useState<string>("main");
  const [pendingTransitionWorkspaceId, setPendingTransitionWorkspaceId] = useState<string | null>(
    null,
  );
  const filteredItems = useSearchList(data, search, "name");
  const mainMenuRef = useRef<HTMLDivElement>(null);
  const {
    boardsByWorkspace,
    fetchBoardsForWorkspace,
    loadingWorkspaceId,
    mutateWorkspaceBoards,
    error,
  } = useWorkspaceBoards();

  // Transition when boards for the pending workspace are ready (hook merges data in its own useEffect, so we react to boardsByWorkspace)
  useEffect(() => {
    if (!pendingTransitionWorkspaceId) return;
    const boards = boardsByWorkspace[pendingTransitionWorkspaceId];
    if (boards !== undefined) {
      setActiveMenu(pendingTransitionWorkspaceId);
      setPendingTransitionWorkspaceId(null);
    }
  }, [pendingTransitionWorkspaceId, boardsByWorkspace]);

  // Clear pending on error so the spinner stops and user can retry
  useEffect(() => {
    if (error && pendingTransitionWorkspaceId) {
      setPendingTransitionWorkspaceId(null);
    }
  }, [error, pendingTransitionWorkspaceId]);

  // Recalculate dropdown height when search or filtered list length changes (main menu visible)
  useEffect(() => {
    if (activeMenu === "main" && mainMenuRef.current) {
      calcHeight(mainMenuRef.current);
    }
  }, [activeMenu, search, filteredItems.length, calcHeight]);

  const handleWorkspaceSelect = (workspaceId: string) => {
    fetchBoardsForWorkspace(workspaceId);
    setPendingTransitionWorkspaceId(workspaceId);
  };

  return (
    <div className="relative w-full">
      <CSSTransition
        in={activeMenu === "main"}
        unmountOnExit
        timeout={500}
        classNames="menu-primary"
        onEnter={calcHeight}
        onEntered={calcHeight}
      >
        <div className="flex flex-col menu" id="add_item_step2" ref={mainMenuRef}>
          <div style={HEADER_PADDING} className={STICKY_HEADER_CLASS}>
            <Search
              placeholder="Search Workspaces"
              onChange={setSearch}
              size="small"
              value={search}
            />
          </div>
          <List
            sx={{ width: "100%", bgcolor: "background.paper" }}
            className="!text-xs"
            component="nav"
          >
            {data.length > 0 &&
              filteredItems.map((workspace, index) => {
                const isLoadingBoards = loadingWorkspaceId === workspace.id;
                return (
                  <ListItemButton
                    key={workspace.id}
                    sx={{
                      "&:hover": { bgcolor: "#E6E9EF" },
                      borderRadius: "4px",
                    }}
                    className="relative"
                    onClick={() => handleWorkspaceSelect(workspace.id)}
                    disabled={isLoadingBoards}
                  >
                    <ListItemIcon sx={{ minWidth: "unset" }} className="!w-auto mr-2">
                      <WorkspaceIcon workspace={workspace} index={index} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={LIST_ITEM_TYPOGRAPHY}
                      primary={workspace.name}
                    />
                    {isLoadingBoards ? (
                      <Spinner size={16} />
                    ) : (
                      <DropdownChevronRight size={16} color="#676879" />
                    )}
                  </ListItemButton>
                );
              })}
          </List>
          <AddItemSubtitle subtitle="Select a workspace to see its boards" />
        </div>
      </CSSTransition>

      {data.length > 0 &&
        data.map((workspace) => (
          <CSSTransition
            key={workspace.id}
            in={activeMenu === workspace.id}
            unmountOnExit
            timeout={500}
            classNames="menu-secondary"
            onEnter={calcHeight}
            onEntered={calcHeight}
          >
            <div className="flex flex-col menu">
              <BoardsList
                navigation={{
                  workspaceName: workspace.name,
                  workspaceId: workspace.id,
                  setActiveMenu,
                }}
                calcHeight={calcHeight}
                options={options}
                boardsFromParent={{
                  boardsData: boardsByWorkspace[workspace.id] ?? [],
                  isLoadingBoards: loadingWorkspaceId === workspace.id,
                  mutateBoardsList: mutateWorkspaceBoards,
                }}
                onGroupSelect={onGroupSelect}
              />
            </div>
          </CSSTransition>
        ))}
    </div>
  );
};

export default WorkspaceList;
